export interface PaymentMethod {
  id: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  stripePaymentMethodId?: string;
  stripeCustomerId?: string;
  type: "card" | "paypal" | "bank_account" | "us_bank_account" | "sepa_bank_account";
  brand?: string;
  last4?: string;
  expMonth?: string;
  expYear?: string;
  bankName?: string;
  accountHolderName?: string;
  routingNumber?: string;
  accountNumber?: string;
  iban?: string;
  bic?: string;
  swift?: string;
  bankCountry?: string;
  accountType?: "personal" | "business";
  paypalEmail?: string;
  isDefault: boolean;
  status: "active" | "inactive";
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentMethodDto {
  stripePaymentMethodId?: string;
  type: "card" | "paypal" | "bank_account" | "us_bank_account" | "sepa_bank_account";
  brand?: string;
  last4?: string;
  expMonth?: string;
  expYear?: string;
  bankName?: string;
  accountHolderName?: string;
  accountHolderType?: "individual" | "company";
  routingNumber?: string;
  accountNumber?: string;
  iban?: string;
  bic?: string;
  swift?: string;
  bankCountry?: string;
  accountType?: "personal" | "business";
  paypalEmail?: string;
  isDefault?: boolean;
  status?: "active" | "inactive";
  metadata?: Record<string, any>;
  country?: string;
}

export interface UpdatePaymentMethodDto {
  isDefault?: boolean;
  status?: "active" | "inactive";
  metadata?: Record<string, any>;
}

export interface Withdrawal {
  id: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  } | null;
  paymentMethod?: {
    id: string;
    type: string;
    brand?: string;
    last4?: string;
    bankName?: string;
    paypalEmail?: string;
  } | null;
  amount?: number | null;
  currency?: string | null;
  status?: number | null; // 1=pending, 2=processing, 3=completed, 4=failed, 5=cancelled
  method?: number | null; // 1=bank, 2=paypal, 3=card
  withdrawalMethod?: "stripe" | "paypal" | "bank_account" | null; // Withdrawal method used
  paypalEmail?: string | null; // PayPal email if PayPal method was used
  paypalPayoutBatchId?: string | null; // PayPal payout batch ID
  bankAccountId?: string | null; // Bank account ID if bank_account method was used
  stripeTransferId?: string | null;
  stripePayoutId?: string | null;
  failureReason?: string | null;
  notes?: string | null;
  processedAt?: Date | null;
  completedAt?: Date | null;
  metadata?: Record<string, any> | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateWithdrawalDto {
  amount: number;
  currency?: string; // Default: USD
  withdrawalMethod?: "stripe" | "paypal" | "bank_account"; // Default: stripe
  paypalEmail?: string; // Required if withdrawalMethod is "paypal"
  bankAccountId?: string; // Required if withdrawalMethod is "bank_account"
  notes?: string;
  metadata?: Record<string, any>;
}

// New interfaces for Stripe Connect status
export interface StripeConnectStatus {
  hasAccount: boolean;
  accountId?: string;
  isEnabled: boolean;
  requirements?: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
    pending_verification: string[];
  };
}

export interface AdminBalance {
  available: number;
  pending: number;
  currency: string;
}

// Updated enums to match backend
export enum WithdrawalStatus {
  PENDING = 1,
  PROCESSING = 2,
  COMPLETED = 3,
  FAILED = 4,
  CANCELLED = 5
}

export enum WithdrawalMethod {
  BANK_ACCOUNT = 1,
  PAYPAL = 2,
  CARD = 3
}

// Status labels for display
export const WithdrawalStatusLabels = {
  [WithdrawalStatus.PENDING]: "Pending",
  [WithdrawalStatus.PROCESSING]: "Processing",
  [WithdrawalStatus.COMPLETED]: "Completed",
  [WithdrawalStatus.FAILED]: "Failed",
  [WithdrawalStatus.CANCELLED]: "Cancelled",
} as const;

export const WithdrawalMethodLabels = {
  [WithdrawalMethod.BANK_ACCOUNT]: "Bank Account",
  [WithdrawalMethod.PAYPAL]: "PayPal",
  [WithdrawalMethod.CARD]: "Card",
} as const;
